import { z } from 'zod'

import type { ApiClient } from '@/shared/api/client'

/** The API path of the request's site, which anyone may read. */
export const currentSitePath = '/site-management/v1/sites/current'

/** An origin as the API stores a base URL: `https`, or `http` for `localhost`, with no path. */
const origin = z.string().refine((value) => {
  try {
    const url = new URL(value)
    return (
      url.origin === value &&
      (url.protocol === 'https:' || (url.protocol === 'http:' && url.hostname === 'localhost'))
    )
  } catch {
    return false
  }
}, 'Not an origin.')

/** What the shell reads of the current site; the rest of the answer is for the items that use it. */
const currentSiteSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  primaryBaseUrl: origin,
  redirectsToPrimaryBaseUrl: z.boolean(),
  // What sign-in sends to B2C.
  signInOptions: z
    .object({ domainHint: z.string().nullish(), customB2CPolicy: z.string().nullish() })
    .optional(),
  // What the branding provider applies to every page (`SiteBrandingApplied`).
  branding: z
    .object({
      logoUrl: z.string().nullish(),
      logoWidthPixels: z.number().int().positive().nullish(),
      logoHeightPixels: z.number().int().positive().nullish(),
      faviconUrl: z.string().nullish(),
      primaryColor: z.string().nullish(),
      secondaryColor: z.string().nullish(),
      navigationLinks: z
        .array(z.object({ label: z.string(), target: z.string(), audience: z.enum(['signedIn', 'everyone']) }))
        .optional(),
      customCss: z.string().nullish(),
      footerHtml: z.string().nullish(),
    })
    .optional(),
})

export type CurrentSite = z.infer<typeof currentSiteSchema>

/** Loads the site the page's origin belongs to. Rejects when there is none, or the API cannot be reached. */
export async function loadCurrentSite(client: ApiClient, signal: AbortSignal): Promise<CurrentSite> {
  return currentSiteSchema.parse(await client.get(currentSitePath, signal))
}

/** How long the lookup may take before the page goes on without it and sign-in is let through. An object, so tests can shorten it. */
export const lookupDeadline = { ms: 10_000 }

/** Moves the page to another address, replacing it in the history. An object, so tests can watch it. */
export const pageNavigation = {
  replace(address: string): void {
    window.location.replace(address)
  },
}

/**
 * Where the page moves to when it is on one of the site's other base URLs and the site sends
 * those to its primary: the same path, query and fragment on the primary. `null` to stay.
 */
export function primaryAddress(site: CurrentSite, location: Location): string | null {
  if (!site.redirectsToPrimaryBaseUrl || location.origin === site.primaryBaseUrl) {
    return null
  }
  return `${site.primaryBaseUrl}${location.pathname}${location.search}${location.hash}`
}
